import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash2,
  Layout,
  Type,
  FileText,
  AlignLeft,
  Info,
  X,
} from "lucide-react";
import LoadingButton from "./LoadingButton";
import { motion, AnimatePresence } from "framer-motion";

export default function ScannerForm({
  onSave,
  initial,
  creating,
  canCreate,
  isEditing,
  onCancel,
}) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [fields, setFields] = useState(
    initial?.fields || [{ name: "", description: "", example: "" }]
  );
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    if (initial) {
      setName(initial.name || "");
      setDescription(initial.description || "");
      let sanitizedFields = (initial.fields || []).map((field) => ({
        name: field.name || "",
        description: field.description || "",
        example: field.example || "",
      }));
      if (sanitizedFields.length === 0) {
        sanitizedFields = [{ name: "", description: "", example: "" }];
      }
      setFields(sanitizedFields);
      setFieldErrors({});
    }
  }, [initial]);

  const addField = () =>
    setFields([...fields, { name: "", description: "", example: "" }]);

  const updateField = (idx, key, value) => {
    setFields(fields.map((f, i) => (i === idx ? { ...f, [key]: value } : f)));
    if (fieldErrors[idx]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });
    }
  };

  const removeField = (idx) => {
    setFields(fields.filter((_, i) => i !== idx));
    if (fieldErrors[idx]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (!name.trim()) {
      setError(t("scanners.error_name_required") || "Name is required");
      return;
    }

    if (!description.trim()) {
      setError(
        t("scanners.error_description_required") || "Description is required"
      );
      return;
    }

    if (fields.length === 0) {
      setError(
        t("scanners.error_fields_required") || "At least one field is required"
      );
      return;
    }

    const newFieldErrors = {};
    let hasFieldErrors = false;

    fields.forEach((f, idx) => {
      const nameValid = f.name && f.name.trim();
      const descriptionValid = f.description && f.description.trim();
      const exampleValid = f.example && f.example.trim();

      if (!nameValid || !descriptionValid || !exampleValid) {
        newFieldErrors[idx] = {
          name: !nameValid,
          description: !descriptionValid,
          example: !exampleValid,
        };
        hasFieldErrors = true;
      }
    });

    if (hasFieldErrors) {
      setFieldErrors(newFieldErrors);
      setError(
        t("scanners.error_field_incomplete") ||
          "All field properties are required."
      );
      return;
    }

    try {
      await onSave({ name, description, fields });
      if (!isEditing) {
        setName("");
        setDescription("");
        setFields([{ name: "", description: "", example: "" }]);
        setFieldErrors({});
      }
    } catch (error) {
      console.error("Failed to save scanner:", error);
      setError(t("scanners.error_save_failed") || "Failed to save scanner");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 sm:space-y-6 md:space-y-8">
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 sm:p-4 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium flex items-center gap-2 mb-3 sm:mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
              <span className="break-words">{error}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6">
        {/* Basic Info Card */}
        <div className="bg-white/50 border border-white/60 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl shadow-sm">
          <h3 className="text-gray-900 font-bold text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
            <Layout className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500 flex-shrink-0" />
            <span>{t("scanners.basic_info") || "Basic Information"}</span>
          </h3>

          <div className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                {t("scanners.name")}
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Type className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-sm sm:text-base text-gray-900 placeholder-gray-400"
                  placeholder={t("scanners.name_placeholder")}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                {t("scanners.description")}
              </label>
              <div className="relative group">
                <div className="absolute top-2.5 sm:top-3 left-3 pointer-events-none">
                  <AlignLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-sm sm:text-base text-gray-900 placeholder-gray-400 resize-none min-h-[80px] sm:min-h-[100px]"
                  placeholder={t("scanners.description_placeholder")}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fields Card */}
        <div className="bg-white/50 border border-white/60 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-gray-900 font-bold text-base sm:text-lg flex items-center gap-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500 flex-shrink-0" />
              <span>{t("scanners.fields")}</span>
            </h3>
            <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2 py-1 rounded-lg flex-shrink-0">
              {t("scanners.fields_count", { count: fields.length })}
            </span>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <AnimatePresence>
              {fields.map((f, idx) => {
                const fieldError = fieldErrors[idx];
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="group relative bg-white border border-gray-100 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary-200"
                  >
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                      <button
                        className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50"
                        type="button"
                        onClick={() => removeField(idx)}
                        title={t("scanners.remove_field")}
                        aria-label={t("scanners.remove_field")}
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 pr-8 sm:pr-10">
                      <div className="col-span-1">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-primary-400 flex-shrink-0"></span>
                          <span>{t("scanners.field_name")}</span>
                        </label>
                        <input
                          placeholder="e.g. Invoice Amount"
                          value={f.name || ""}
                          onChange={(e) =>
                            updateField(idx, "name", e.target.value)
                          }
                          className={`w-full bg-gray-50 border text-xs sm:text-sm font-semibold text-gray-800 rounded-lg px-2.5 sm:px-3 py-2 sm:py-2.5 outline-none transition-all ${
                            fieldError?.name
                              ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100 bg-red-50"
                              : "border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:bg-white"
                          }`}
                        />
                      </div>

                      <div className="col-span-1">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0"></span>
                          <span>{t("scanners.field_example")}</span>
                        </label>
                        <input
                          placeholder="e.g. $1,250.00"
                          value={f.example || ""}
                          onChange={(e) =>
                            updateField(idx, "example", e.target.value)
                          }
                          className={`w-full bg-gray-50 border text-xs sm:text-sm font-medium text-gray-700 rounded-lg px-2.5 sm:px-3 py-2 sm:py-2.5 outline-none transition-all ${
                            fieldError?.example
                              ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100 bg-red-50"
                              : "border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:bg-white"
                          }`}
                        />
                      </div>

                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                          <Info className="w-3 h-3 flex-shrink-0" />
                          <span>{t("scanners.field_description")}</span>
                        </label>
                        <input
                          placeholder={t("uiFixes.field_description_placeholder")}
                          value={f.description || ""}
                          onChange={(e) =>
                            updateField(idx, "description", e.target.value)
                          }
                          className={`w-full bg-gray-50 border text-xs sm:text-sm text-gray-600 rounded-lg px-2.5 sm:px-3 py-2 sm:py-2.5 outline-none transition-all ${
                            fieldError?.description
                              ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100 bg-red-50"
                              : "border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:bg-white"
                          }`}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={addField}
            className="mt-4 sm:mt-6 w-full py-2.5 sm:py-3 border-2 border-dashed border-gray-200 rounded-lg sm:rounded-xl text-gray-500 font-bold text-xs sm:text-sm hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            <div className="bg-gray-100 group-hover:bg-white p-1 rounded-md transition-colors">
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
            <span>{t("scanners.add_field")}</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-100">
        {(isEditing || onCancel) && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 rounded-lg sm:rounded-xl font-bold shadow-sm transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>{t("common.cancel") || "Cancel"}</span>
          </button>
        )}
        <LoadingButton
          type="submit"
          loading={creating}
          className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-lg sm:rounded-xl font-bold shadow-lg shadow-primary-600/20 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300 transform hover:-translate-y-0.5 text-sm sm:text-base"
        >
          {isEditing
            ? t("scanners.update") || "Update Scanner"
            : t("scanners.create") || "Create Scanner"}
        </LoadingButton>
      </div>
    </form>
  );
}
